import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { trackCheckoutStart } from './lib/tracking';
import { captureAttribution } from './lib/attribution';
import { ctaEvent, landingVariant, sessionUtm, trackLanding } from './lib/landing';
import { useLang } from './i18n/LanguageContext';

const CheckoutContext = createContext();

export const CheckoutProvider = ({ children }) => {
  // Langue réellement affichée (détection auto incluse) : Stripe s'ouvre dans la même.
  const { lang } = useLang();
  const [isActive, setIsActive] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  useEffect(() => {
    const markLeft = () => sessionStorage.setItem('openbro_left_for_checkout', '1');
    const clearPortalOnReturn = () => {
      if (sessionStorage.getItem('openbro_left_for_checkout') !== '1') return;
      sessionStorage.removeItem('openbro_left_for_checkout');
      setIsActive(false);
      setCheckoutUrl(null);
    };
    window.addEventListener('pagehide', markLeft);
    window.addEventListener('pageshow', clearPortalOnReturn);
    return () => {
      window.removeEventListener('pagehide', markLeft);
      window.removeEventListener('pageshow', clearPortalOnReturn);
    };
  }, []);

  // options.locale : langue de Stripe imposée par la page (/eat est en anglais, quelle que soit la langue du navigateur).
  const startCheckout = useCallback(async (buttonId = 'unknown', booking = null, options = {}) => {
    setIsActive(true);
    setCheckoutUrl(null);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const origin = window.location.origin;
      trackCheckoutStart(buttonId);
      trackLanding(ctaEvent(buttonId), { button: buttonId });
      const locale = (options.locale || lang) === 'en' ? 'en' : 'fr';
      const body = {
        origin_url: origin,
        locale,
        source: buttonId || 'unknown',
        page: window.location.pathname,
        referrer: document.referrer || '',
        screen: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language || '',
        attribution: captureAttribution(),
        // Landing comparée (prix propre à /eat) et UTMs de cette visite : jusqu'à la metadata Stripe.
        landing_variant: landingVariant() || undefined,
        utm: sessionUtm(),
      };
      // /v60 : pas de créneau avant de payer ; le Worker demande le téléphone et le créneau se choisit après.
      if (booking?.bookLater) body.book_later = true;
      if (booking?.callAt && booking?.callLabel) {
        body.call_at = booking.callAt;
        body.call_label = booking.callLabel;
        body.call_tz = booking.callTz || '';
      }
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Erreur paiement');
      const data = await res.json();
      if (data.url) {
        setCheckoutUrl(data.url);
      }
    } catch (e) {
      console.error('Checkout error:', e);
      setIsActive(false);
      alert(lang === 'en' ? 'Something went wrong opening checkout. Please try again.' : 'Erreur lors de la redirection. Réessaie.');
    }
  }, [lang]);

  return (
    <CheckoutContext.Provider value={{ isActive, checkoutUrl, startCheckout, setIsActive }}>
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = () => useContext(CheckoutContext);
